
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { ConnectedProps, connect, useSelector } from "react-redux";
import { Card, CardBody, Col, Row } from "reactstrap";
import { AnyAction, Dispatch } from "redux";
import { ICredentials, IModel, IProject, IProjectMembership, IUser, UserRole } from "@api/schema";
import BaseLayout from "@components/BaseLayout";
import PageError from "@components/common/PageError";
import TranslatedHtml from "@components/common/TranslatedHtml";
import { followershipsListCardHashId } from "@components/usecases/my-projects/MyFollowershipsCard";
import LoginForm from "@components/user/LoginForm";
import { triggerNotificationForUnusedIdeaAction } from "@redux/actions/idea";
import { IFormikActions } from "@redux/helper/actions";
import { AppState } from "@redux/reducer";
import { selectIsAuthenticated } from "@redux/reducer/auth";
import { OnboardingType, selectOnboardingType } from "@redux/usecases/onboarding";
import { OnOnboardingDataAfterLoginErrorCallback, OnOnboardingDataAfterLoginSuccessCallback, loginAction, processOnboardingDataAfterLoginAction, selectLoginUsecaseState } from "@redux/usecases/userAccount";
import { Routes, getStringFromQuery, routeWithParams } from "@services/routes";
import { hasMatches, idFromIModelOrIRI, logUnhandledUnexpectedError } from "@services/util";
import { ideaListCardHashId } from "./projects";
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    doLogin: (credentials: ICredentials, actions: IFormikActions) => dispatch(loginAction(credentials, actions)),
    processOnboardingData: (user: IUser, onSuccess: OnOnboardingDataAfterLoginSuccessCallback, onError: OnOnboardingDataAfterLoginErrorCallback) => dispatch(processOnboardingDataAfterLoginAction(user, onSuccess, onError)),
    triggerNotificationForUnusedIdea: () => dispatch(triggerNotificationForUnusedIdeaAction()),
});
const mapStateToProps = (state: AppState) => ({
    isAuthenticated: selectIsAuthenticated(state),
    loginState: selectLoginUsecaseState(state),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type PageProps = ConnectedProps<typeof connector>;
/**
 * this page provides a login-form for the user and handles actions after the login,
 * especially:
 * - forwarding to the page the user wanted to visit
 * - forwarding to a fresh created project
 */
/**
 * Several use cases are involved in a successful logging in:
 * 0/in any case: The user has an idea without converting it into a project. Therefor the user will be notified.
 * 1. user is logging in from login-page and will be redirected to the project dashboard.
 * 2. user is processmanager and will be redirected to the manager dashboard
 * 3. user is logging in from a different page than login-page (redirect) and will be redirect back, e.g. SinglePostForm (Feedback).
 * 4. user has created something before logging in - this is handled by processOnboardingData, we handle routing
 * 4.1 user has created an idea and will be redirected to the project dashboard to the ideas.
 * 4.2 user has created a membership and will be redirected to the project dashboard.
 * 4.3 user has created a project and will be redirect to the projectprofile edit page.
 */
const LoginPage: React.FC<PageProps> = (props: PageProps) => {
    const { doLogin, processOnboardingData, triggerNotificationForUnusedIdea, isAuthenticated, loginState } = props;
    const { t } = useTranslation("common");
    const router = useRouter();
    const redirectBack = getStringFromQuery(router, "redirectBack");
    const onboardingDataExists = useSelector(selectOnboardingType) !== OnboardingType.None;
    // prevent redirecting to the profile when the state changes while a login is running
    const [loggingIn, setLoggingIn] = useState(false);
    if (!loggingIn && isAuthenticated) {
        void router.push(Routes.UserProfile);
        return null;
    }
    const onSubmit = (credentials: ICredentials, actions: IFormikActions): void => {
        // define the function that is called, when the login was successfull:
        // do actions directly after login
        actions.onSuccess = (loggedInUser: IUser) => {
            /**
             * Routes the user to the most interesting page after login.
             * There are 3 main categories ordered by descanding priority:
             * 1) login page has been accessed with `redirectBack` parameter - route there
             * 2) login page had some special actions (e.g. onboarding) leading to another routing (other than default) - route to otherRoute
             * 3) default handling - route to personal or management dashboard, dependent on user role
             *
             * @param otherRoute A non-default route that is chosen with medium priority.
             */
            const performLoginRouting = (otherRoute: string = null) => {
                // category 1) login page has been accessed with `redirectBack` parameter
                if (redirectBack) {
                    // redirectBack into the management-area should only be possible für ProcessManager + Admin to avoid 404.
                    // since redirectBack may include a whole URL (with https://) -> check with .includes instead of .startsWith
                    if (redirectBack.toLocaleLowerCase().includes("/management")) {
                        // if the user is allowed to enter the manegement area redirect him to the wanted page
                        // otherwise let the default routing happen
                        if (hasMatches(loggedInUser.roles, [UserRole.ProcessManager, UserRole.Admin])) {
                            void router.push(redirectBack);
                            return;
                        }
                    }
                    else {
                        // if redirectBack is given but without aiming for the management area, route the user to the given URL without checking permissions
                        void router.push(redirectBack);
                        return;
                    }
                }
                // category 2) login page had some special actions
                if (otherRoute) {
                    void router.push(otherRoute);
                    return;
                }
                // category 3) default handling
                // if the (logged in) user is ProcessManager send him to the ProcessManager-Dashboard by default
                if (loggedInUser.roles.includes(UserRole.ProcessManager)) {
                    void router.push(Routes.AdminDashboard);
                    return;
                }
                // if not pushed somewhere else, send the user to his project-dashboard by default
                // will be directed after the user just logged in
                void router.push(Routes.MyProjects);
            };
            // trigger notification for unused ideas. The check if unused ideas exists is done in the saga
            triggerNotificationForUnusedIdea();
            if (onboardingDataExists) {
                processOnboardingData(loggedInUser, 
                // onSuccess
                (onboardingType: OnboardingType, createdModel: IModel) => {
                    let onboardingRoute: string = null;
                    switch (onboardingType) {
                        case OnboardingType.NewIdea:
                            onboardingRoute = Routes.MyProjects + "#" + ideaListCardHashId;
                            break;
                        case OnboardingType.NewProjectFromIdea:
                            const createdProject = createdModel as IProject;
                            onboardingRoute = routeWithParams(Routes.ProjectProfileEdit, { slug: createdProject.id });
                            break;
                        case OnboardingType.NewProjectMemberApplication:
                            const createdProjectMembership = createdModel as IProjectMembership;
                            onboardingRoute = Routes.MyProjects + "#project-" + idFromIModelOrIRI(createdProjectMembership.project).toString();
                            break;
                        case OnboardingType.NewProjectFollowership:
                            onboardingRoute = Routes.MyProjects + "#" + followershipsListCardHashId;
                            break;
                    }
                    performLoginRouting(onboardingRoute);
                }, 
                // onError
                (error: string) => {
                    logUnhandledUnexpectedError(error, 'server');
                    performLoginRouting();
                });
                return;
            }
            performLoginRouting();
        }; // end: actions.onSuccess
        setLoggingIn(true);
        doLogin(credentials, actions);
    };
    return <BaseLayout pageTitle={t("page.user.login.title")}>
    <Row>
      <Col sm={8}>
        <Card>
          <CardBody>
            <PageError error={loginState?.loadingError}/>
            <LoginForm onSubmit={onSubmit}/>
            <br />
            <Link href={Routes.ForgotPassword}>
              <a>{t("goto.forgotPassword")}</a>
            </Link>
            <br />
            <br />
            <p>
              <TranslatedHtml content="page.user.login.registerInfo"/>
              <br />
              <Link href={Routes.Marketplace}>
                <a>{t("goto.marketplace")}</a>
              </Link>
            </p>
          </CardBody>
        </Card>
      </Col>
    </Row>
  </BaseLayout>;
};
const __Next_Translate__Page__18c4f5179ef__ = connector(LoginPage);

    export default __appWithI18n(__Next_Translate__Page__18c4f5179ef__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  